import React from "react";
import Nullable from "@data-force/app/interfaces/nullable";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {InfoCircleOutlined, CloseOutlined} from "@ant-design/icons";
import "./JobInfo.scss";
import StoreState from "../../../interfaces/store/storeState";
import {calculateStatusData} from "../../../utils/common";
import {StatusData} from "../../../interfaces/store/actions/payload/job";
import {ContentType} from "../../../interfaces/store/actions/payload/layout";
import {setContentType, setJobSectionInstructionsVisible} from "../../../store/actions/layout";
import {SetContentTypeAction, SetJobSectionInstructionsVisible} from "../../../interfaces/store/actions/layout";
import {StatusStats} from "../../../interfaces/store/actions/payload/task";
import {ENTER_KEY, SPACE_KEY} from "../../../utils/constants";
import UniqueJobId from "./UniqueJobId/UniqueJobId";

interface StateProps {
    jobName: string
    statusStats: Nullable<StatusStats>
    rejectedTaskCount: number
    translate: TranslateFunction
    instructionsInfoVisible: boolean
    numberOfTasks: number
}

interface DispatchProps {
    setContentType(payload: ContentType): SetContentTypeAction

    setJobSectionInstructionsVisible(payload: boolean): SetJobSectionInstructionsVisible
}

type Props = StateProps & DispatchProps;

class JobInfo extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobInfo.JobInfo";

    constructor(props: Props) {
        super(props);
        this.displayInstructions = this.displayInstructions.bind(this);
        this.hideJobInfoInstructionsInfo = this.hideJobInfoInstructionsInfo.bind(this);
        this.onDisplayInstructions = this.onDisplayInstructions.bind(this);
        this.onHideJobInfoInstructionsInfo = this.onHideJobInfoInstructionsInfo.bind(this);
    }

    /**
     * Display instruction page
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onDisplayInstructions(event: React.KeyboardEvent): void {
        if (event.key === ENTER_KEY) {
            this.displayInstructions();
        }
    }

    /**
     * Hide instructions info
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onHideJobInfoInstructionsInfo(event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.props.setJobSectionInstructionsVisible(false);
        }
    }

    displayInstructions() {
        this.props.setContentType(ContentType.INSTRUCTIONS);
    }

    hideJobInfoInstructionsInfo() {
        this.props.setJobSectionInstructionsVisible(false);
    }

    render() {
        const {
            jobName, statusStats, rejectedTaskCount, translate, instructionsInfoVisible, numberOfTasks,
        }: Props = this.props;
        const {translationPath}: typeof JobInfo = JobInfo;
        const {statusClass, statusName}: StatusData = calculateStatusData({
            statusStats,
            rejectedTaskCount,
            translate,
            translationPath,
            numberOfTasks,
        });

        return (
            <div className="df-job-info">
                {instructionsInfoVisible
                && (
                    <div className="df-instructions-info">
                        <InfoCircleOutlined className="df-instructions-icon"/>
                        <div className="df-instructions-text">
                            <h2 className="df-instructions-title">
                                <Translate id={`${translationPath}.requirementsAndInstructions.text`}/>
                            </h2>
                            <div className="df-instructions-link-holder">
                                <span>
                                    <Translate id={`${translationPath}.instructionsInfo.text`}/>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <a
                                        className="df-instructions-link"
                                        onClick={this.displayInstructions}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={this.onDisplayInstructions}
                                    >
                                        <Translate id={`${translationPath}.here.text`}/>
                                    </a>
                                    .
                                </span>
                            </div>
                        </div>
                        <CloseOutlined
                            className="df-close-icon"
                            onClick={this.hideJobInfoInstructionsInfo}
                            tabIndex={0}
                            onKeyDown={this.onHideJobInfoInstructionsInfo}
                        />
                    </div>
                )}
                <div className="df-job-name">
                    <span
                        className={`df-job-status-span ${statusClass}`}
                        title={statusName}
                        aria-label={statusName}
                    />
                    {jobName}
                </div>
                <UniqueJobId/>
            </div>
        );
    }
}

function mapStateToProps(
    {
        job: {
            jobDetails,
            jobPhase,
            tasks,
        },
        localize,
        layout: {
            jobSection: {
                instructionsVisible,
            },
        },
    }: StoreState,
): StateProps {
    return {
        jobName: jobDetails?.name || "",
        statusStats: jobPhase?.phaseStats,
        rejectedTaskCount: jobPhase?.taskStats.REJECTED || 0,
        translate: getTranslate(localize),
        instructionsInfoVisible: instructionsVisible,
        numberOfTasks: tasks.length,
    };
}

const mapDispatchToProps: DispatchProps = {
    setContentType,
    setJobSectionInstructionsVisible,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobInfo);
